
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, 
  Camera, 
  RotateCcw, 
  Trash2,
  AlertTriangle 
} from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';

interface User {
  id: string;
  username: string;
  city: string;
  state_code: string;
  main_photo_url?: string;
  is_online: boolean;
  onboarding_completed: boolean;
  hidden: boolean;
  created_at: string;
  verification_level: string;
  last_seen?: string;
}

interface UserActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSendNotification: (userId: string, title: string, message: string) => void;
  onManagePhotos: (userId: string) => void;
  onForceOnboarding: (userId: string) => void;
}

const UserActionsModal: React.FC<UserActionsModalProps> = ({
  isOpen,
  onClose,
  user,
  onSendNotification,
  onManagePhotos,
  onForceOnboarding
}) => {
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetAccountDialog, setResetAccountDialog] = useState(false);

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) return;

    setIsLoading(true);
    try {
      await onSendNotification(user.id, notificationTitle, notificationMessage);
      setNotificationTitle('');
      setNotificationMessage('');
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      debugLogger.error('Erro ao enviar notificação:', error);
      toast.error('Erro ao enviar notificação', {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceOnboarding = async () => {
    setIsLoading(true);
    try {
      await onForceOnboarding(user.id);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      debugLogger.error('Erro ao forçar onboarding:', error);
      toast.error('Erro ao forçar onboarding', {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetAccount = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.rpc('reset_user_account', {
        target_user_id: user.id
      });
      
      if (error) {
        debugLogger.error('Erro detalhado ao resetar conta:', error);
        throw new Error(error.message || 'Erro desconhecido ao resetar conta');
      }
      
      toast.success('Conta resetada com sucesso');
      setResetAccountDialog(false);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      debugLogger.error('Erro ao resetar conta:', error);
      toast.error('Erro ao resetar conta', {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-black border-white/10 text-white max-w-md" hasTitle={true}>
          <DialogHeader>
            <DialogTitle className="text-xl">
              Ações para {user.username}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Envie notificações, gerencie fotos e execute ações administrativas para este usuário
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Send Notification Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-purple-400" />
                <Label className="text-lg font-medium">Enviar Notificação</Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Título da notificação"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mensagem</Label>
                <Textarea
                  id="message"
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  placeholder="Digite sua mensagem aqui..."
                  rows={3}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              <Button
                onClick={handleSendNotification}
                disabled={!notificationTitle.trim() || !notificationMessage.trim() || isLoading}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <Bell className="h-4 w-4 mr-2" />
                {isLoading ? 'Enviando...' : 'Enviar Notificação'}
              </Button>
            </div>

            <Separator className="bg-white/10" />

            {/* Other Actions */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">Outras Ações</Label>
              
              <div className="grid grid-cols-1 gap-2">
                <Button
                  variant="outline"
                  onClick={() => onManagePhotos(user.id)}
                  className="justify-start bg-blue-600/20 border-blue-600/50 text-blue-300 hover:bg-blue-600/30"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Gerenciar Fotos
                </Button>

                <Button
                  variant="outline"
                  onClick={handleForceOnboarding}
                  disabled={isLoading}
                  className="justify-start bg-orange-600/20 border-orange-600/50 text-orange-300 hover:bg-orange-600/30"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {isLoading ? 'Processando...' : 'Forçar Onboarding'}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setResetAccountDialog(true)}
                  disabled={isLoading}
                  className="justify-start bg-red-600/20 border-red-600/50 text-red-300 hover:bg-red-600/30"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Zerar Conta
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Account Confirmation Dialog */}
      <AlertDialog open={resetAccountDialog} onOpenChange={setResetAccountDialog}>
        <AlertDialogContent className="bg-black border-red-500/50" hasTitle>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Confirmar Reset da Conta
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/80">
              <strong>ATENÇÃO:</strong> Esta ação é <strong>IRREVERSÍVEL</strong> e irá:
              <br /><br />
              • Resetar o onboarding para incompleto
              <br />
              • Apagar TODOS os dados do perfil (exceto username e ID)
              <br />
              • Remover todas as conexões e conversas
              <br />
              • Deletar todas as mensagens enviadas
              <br />
              • Limpar notificações e verificações
              <br />
              • Remover visualizações de perfil
              <br /><br />
              Usuário: <strong>{user.username}</strong>
              <br /><br />
              Tem certeza que deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetAccount}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? 'Resetando...' : 'Sim, Zerar Conta'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserActionsModal;
